import { CMS_API_URL } from '../config/apiBase';
import { findMenuDestination, pickGroup } from './menuDestination';

/**
 * Resolve a landing-page click (a section CTA or one product / package card)
 * to the customer's OWN school shop. Pure decision + one variations fetch:
 *
 *  1. the shop to enter = the explicitly chosen shop (popup selection), else
 *     the school's open shop mapped to the section's menu item
 *     ('menu_1' | 'menu_2'), else ANY open shop of the school (the stored one
 *     first) — never the brand's school-less "open shop";
 *  2. inside that shop, the clicked product / package when it sells it → its
 *     configurator; otherwise the shop page;
 *  3. a school with no open shop at all → `closedPath` (the closed page).
 *
 * `school.seasons` ARE the school's open shops (the public API's naming).
 *
 * @param {object}  args
 * @param {string}  args.brandSlug   API slug ('clsdk')
 * @param {string}  args.feSlug      route slug ('cls-dk')
 * @param {object}  args.school      { slug, seasons: [{id, slug, menuItemKey}] }
 * @param {string}  [args.menuKey]   the section's menu item
 * @param {object}  [args.chosenShop]   shop picked in the popup (wins)
 * @param {object}  [args.storedShop]   shop of the stored selection (any-shop tiebreak)
 * @param {string}  [args.className]    class for the variations request
 * @param {object}  [args.target]    { type: 'product', id, productId, name } | { type: 'package', id, slug }
 * @param {string}  args.closedPath  where to go when the school has no open shop
 * @returns {Promise<string>} internal path to navigate to
 */
export async function resolveShopDestination({
  brandSlug, feSlug, school, menuKey = null, chosenShop = null, storedShop = null,
  className = null, target = null, closedPath,
}) {
  const shops = Array.isArray(school?.seasons) ? school.seasons.filter((s) => s?.slug) : [];
  const schoolSlug = school?.slug;
  if (!schoolSlug || shops.length === 0) return closedPath;

  const same = (a, b) => Boolean(a && b && ((a.id != null && b.id != null && String(a.id) === String(b.id)) || (a.slug && a.slug === b.slug)));
  const chosen = chosenShop ? shops.find((s) => same(s, chosenShop)) : null;
  // The shop whose group / that is mapped to the menu item (services/menuDestination),
  // else the stored shop, else the first open one.
  const mapped = menuKey ? findMenuDestination({ seasons: shops }, menuKey).shop : null;
  const wanted = chosen ?? mapped ?? null;
  const shop = wanted ?? shops.find((s) => same(s, storedShop)) ?? shops[0];
  // Inside that shop: the group mapped to the menu item, else its first group
  // (the root is never the target when the shop has groups), else the root.
  const group = pickGroup(shop, menuKey);

  const base = `/clothing/${feSlug}/school-shop/${encodeURIComponent(schoolSlug)}`;
  const shopPath = `${base}/${encodeURIComponent(shop.slug)}${group?.slug ? `/${encodeURIComponent(group.slug)}` : ''}`;
  // Look for the clicked item in whichever shop we resolved to — with the
  // Vogntrøjer collections merged into the Studieturstrøjer shops (2026-09),
  // a school's single shop sells both, so the fallback shop can carry it too.
  if (!target) return shopPath;

  try {
    const qs = new URLSearchParams({ shop: shop.slug });
    // A menu section's click looks inside that section's group. A click with NO menu item
    // (the product page's school + class picker) looks in the whole shop — the product may
    // sit in any group, and the first group must not hide it.
    if (group?.slug && menuKey) qs.set('group', group.slug);
    if (className) qs.set('class', className);
    const res = await fetch(
      `${CMS_API_URL}/api/schools/${encodeURIComponent(brandSlug)}/${encodeURIComponent(schoolSlug)}/variations?${qs}`,
    );
    if (!res.ok) return shopPath;
    const json = await res.json();
    if (json?.shopClosed) return shopPath;
    const shopQuery = `?shop=${encodeURIComponent(shop.slug)}`;

    if (target.type === 'package') {
      const pkgName = String(target.name ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
      const pkg = (json.packages || []).find(
        (p) => (target.id != null && String(p.id) === String(target.id)) || (target.slug && p.slug === target.slug),
      ) ?? (target.id == null && pkgName
        ? (json.packages || []).find((p) => String(p.name ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim() === pkgName)
        : undefined);
      return pkg?.slug ? `${base}/product-package/${encodeURIComponent(pkg.slug)}${shopQuery}` : shopPath;
    }

    // Same product in THIS shop's collection: the identical collection item
    // first, then the same card name (several variations of one product),
    // then any variation of the product. A card WITHOUT a product id (the
    // landing's hardcoded demo cards) is matched by its name alone —
    // case / punctuation-insensitive ("STANDARD - HOODIE" = "Standard – Hoodie").
    const norm = (v) => String(v ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    const variations = json.variations || [];
    const wantedName = norm(target.name);
    const ofProduct = variations.filter(
      (v) => target.productId != null && String(v.product_id) === String(target.productId),
    );
    const variation = ofProduct.find((v) => String(v.id) === String(target.id))
      ?? ofProduct.find((v) => wantedName && norm(v.name) === wantedName)
      ?? ofProduct[0]
      ?? (target.productId == null && wantedName
        ? variations.find((v) => norm(v.name) === wantedName)
        : undefined);
    return variation?.id ? `${base}/product/${encodeURIComponent(variation.id)}${shopQuery}` : shopPath;
  } catch {
    return shopPath;
  }
}
